import { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../config/apiConfig';

const PortfolioContext = createContext();

// Default portfolio data fallback
const defaultPortfolioData = {
  profile: {
    name: "Dhiraj T H",
    roles: ["AI Engineer", "Full-Stack Developer", "YouTuber", "Innovator"],
    location: "Bangalore, India",
    email: "dhiraj@example.com",
    phone: "123-456-7890",
    image_url: "/profile-image.png",
    tagline: "Building AI and web solutions.",
    button_text: "Contact Me",
    button_link: "/contact",
    show_button: true
  },
  about: {
    summary: "I am an AI engineer and full-stack developer with a passion for building intelligent applications and sharing knowledge on YouTube.",
    highlights: ["AI/ML", "Web Development", "Mechanical Innovations"]
  },
  skills: [
    { category: "AI/ML", items: ["Python", "TensorFlow", "PyTorch"] },
    { category: "Web", items: ["React", "Node.js", "Express"] }
  ],
  experience: [],
  education: [],
  blog: [],
  youtube: [],
  projects: [],
  innovations: [],
  testimonials: [
    { id: 1, name: "Sarah Johnson", position: "CTO, TechVision Inc", text: "Dhiraj delivered an exceptional AI solution that transformed our data processing workflow.", image: "/testimonials/testimonial-1.jpg" }
  ],
  collaborate: [],
  patents: [],
  footer: {
    copyright: "© 2023 Dhiraj T H. All rights reserved.",
    social_links: [
      { name: "GitHub", url: "https://github.com/dhirajth", icon: "fab fa-github" },
      { name: "LinkedIn", url: "https://www.linkedin.com/in/dhirajth", icon: "fab fa-linkedin" },
      { name: "YouTube", url: "https://www.youtube.com/@dhirajth", icon: "fab fa-youtube" },
      { name: "Twitter", url: "https://twitter.com/dhirajth", icon: "fab fa-twitter" },
      { name: "Instagram", url: "https://www.instagram.com/dhirajth", icon: "fab fa-instagram" }
    ]
  }
};

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};

// Utility: Group skills by category for UI
const groupSkillsByCategory = (skills) => {
  return (skills || []).reduce((acc, skill) => {
    if (!acc[skill.category]) acc[skill.category] = [];
    acc[skill.category].push(skill);
    return acc;
  }, {});
};
// Utility: Flatten grouped skills back to flat array
const flattenGroupedSkills = (groupedSkills) => {
  return Object.values(groupedSkills || {}).flat();
};

// Utility: Validate portfolio data structure
const isValidPortfolio = (data) => {
  return data &&
    typeof data === 'object' &&
    data.profile &&
    data.skills &&
    Object.keys(data).length > 0;
};

export const PortfolioProvider = ({ children }) => {
  const [portfolioData, setPortfolioData] = useState(null);
  const [groupedSkills, setGroupedSkills] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper to get fallback data from localStorage (used by frontend)
  const getFallbackData = () => {
    try {
      const data = localStorage.getItem('portfolioData');
      if (data) return JSON.parse(data);
    } catch {}
    // If nothing in localStorage, return default data
    return defaultPortfolioData;
  };

  const fetchPortfolio = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_BASE_URL}/portfolio`);
      
      // Get response as text first
      const text = await res.text();
      
      // Check if response is empty
      if (!text || text.trim() === '') {
        throw new Error('Portfolio fetch failed: empty response');
      }
      
      let json;
      try {
        json = JSON.parse(text);
      } catch (parseErr) {
        throw new Error(`Failed to parse JSON: ${parseErr.message}`);
      }
      
      // Validate the response structure
      if (json.success === true && json.data && isValidPortfolio(json.data)) {
        setPortfolioData(json.data);
        setGroupedSkills(groupSkillsByCategory(json.data.skills || []));
        // Store successful data in localStorage for fallback
        try {
          localStorage.setItem('portfolioData', JSON.stringify(json.data));
        } catch (storageErr) {
          console.warn('Failed to store portfolio data in localStorage:', storageErr);
        }
      } else {
        // Use fallback data if response is invalid
        const fallbackData = getFallbackData();
        setPortfolioData(fallbackData);
        setGroupedSkills(groupSkillsByCategory(fallbackData.skills || []));
        setError(json.error || 'Invalid portfolio data structure, using fallback.');
        toast.warn('Using fallback data due to invalid response structure.');
      }
    } catch (err) {
      console.error('Portfolio fetch error:', err);
      const fallbackData = getFallbackData();
      setPortfolioData(fallbackData);
      setGroupedSkills(groupSkillsByCategory(fallbackData.skills || []));
      setError(err.message);
      toast.error(`Failed to load portfolio: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Retry logic for failed fetches
  const fetchWithRetry = async () => {
    try {
      await fetchPortfolio();
    } catch (err) {
      console.warn('First fetch failed, retrying in 1s...', err);
      setTimeout(async () => {
        try {
          await fetchPortfolio();
        } catch (e2) {
          console.error('Retry also failed:', e2);
          toast.error('Failed to load portfolio after retry. Please refresh the page.');
        }
      }, 1000);
    }
  };

  useEffect(() => {
    fetchWithRetry();
  }, []);

  // In PortfolioProvider, after portfolioData is loaded:
  // const groupedSkills = groupSkillsByCategory(portfolioData?.skills || []); // This line is removed as per edit hint

  const updatePortfolioData = async (section, data, id = null) => {
    try {
      let res, json;
      switch (section) {
        case 'about':
          res = await fetch(`${API_BASE_URL}/about`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'skills':
          // Always flatten grouped skills before saving
          const flatSkills = Array.isArray(data) ? data : flattenGroupedSkills(data);
          res = await fetch(`${API_BASE_URL}/skills`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(flatSkills)
          });
          break;
        case 'skills-update':
          res = await fetch(`${API_BASE_URL}/skills/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'projects':
          res = await fetch(`${API_BASE_URL}/projects`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'projects-update':
          res = await fetch(`${API_BASE_URL}/projects/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'experience':
          res = await fetch(`${API_BASE_URL}/experience`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'experience-update':
          res = await fetch(`${API_BASE_URL}/experience/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'education':
          res = await fetch(`${API_BASE_URL}/education`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'education-update':
          res = await fetch(`${API_BASE_URL}/education/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'education-delete':
          res = await fetch(`${API_BASE_URL}/education/${id}`, {
            method: 'DELETE'
          });
          break;
        case 'sections':
          res = await fetch(`${API_BASE_URL}/sections`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'sections-update':
          res = await fetch(`${API_BASE_URL}/sections/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'youtube':
          res = await fetch(`${API_BASE_URL}/youtube`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'youtube-update':
          res = await fetch(`${API_BASE_URL}/youtube/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'youtube-delete':
          res = await fetch(`${API_BASE_URL}/youtube/${id}`, {
            method: 'DELETE'
          });
          break;
        case 'blog':
          res = await fetch(`${API_BASE_URL}/blog`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'blog-update':
          res = await fetch(`${API_BASE_URL}/blog/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'blog-delete':
          res = await fetch(`${API_BASE_URL}/blog/${id}`, {
            method: 'DELETE'
          });
          break;
        case 'collaborate':
          res = await fetch(`${API_BASE_URL}/messages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'collaborate-delete':
          res = await fetch(`${API_BASE_URL}/messages/${id}`, {
            method: 'DELETE'
          });
          break;
        case 'testimonials':
          res = await fetch(`${API_BASE_URL}/testimonials`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'testimonials-update':
          res = await fetch(`${API_BASE_URL}/testimonials/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          break;
        case 'testimonials-delete':
          res = await fetch(`${API_BASE_URL}/testimonials/${id}`, {
            method: 'DELETE'
          });
          break;
        case 'profile':
          // Only send fields expected by the backend
          const profilePayload = {
            name: data.name,
            tagline: data.tagline,
            description: data.description,
            roles: data.roles,
            image_url: data.image_url,
            button_text: data.button_text,
            button_link: data.button_link,
            show_button: data.show_button,
            location: data.location,
            email: data.email,
            phone: data.phone
          };
          res = await fetch(`${API_BASE_URL}/profile`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(profilePayload)
          });
          break;
        default:
          throw new Error('Unknown section');
      }
      json = await res.json();
      if (json.success) {
        await fetchPortfolio(); // Refresh all data after update
        if (section === 'profile') toast.success('Profile updated!');
        if (section.startsWith('education')) toast.success('Education updated!');
      } else {
        if (section === 'profile') toast.error(json.error || 'Failed to update profile');
        if (section.startsWith('education')) toast.error(json.error || 'Failed to update education');
        throw new Error(json.error || 'Failed to update section');
      }
    } catch (err) {
      setError(err.message);
      if (section === 'profile') toast.error('Error updating profile: ' + err.message);
    }
  };

  const fetchAllSkills = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/skills?all=true`);
      const json = await res.json();
      if (json.success) {
        setPortfolioData((prev) => ({ ...prev, skills: json.data }));
        setGroupedSkills(groupSkillsByCategory(json.data));
      }
    } catch {}
  };

  // --- SKILLS CRUD HELPERS ---
  // Add Skill
  const addSkill = async (skill) => {
    try {
      const res = await fetch(`${API_BASE_URL}/skills`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(skill)
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllSkills();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };
  // Update Skill
  const updateSkill = async (id, skill) => {
    try {
      const res = await fetch(`${API_BASE_URL}/skills/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(skill)
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllSkills();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };
  // Delete Skill
  const deleteSkill = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/skills/${id}`, {
        method: 'DELETE'
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllSkills();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // --- PATENTS CRUD HELPERS ---
  const fetchAllPatents = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/patents?all=true`);
      const json = await res.json();
      if (json.success) {
        setPortfolioData((prev) => ({ ...prev, patents: json.data }));
      }
    } catch {}
  };
  const addPatent = async (patent) => {
    try {
      const res = await fetch(`${API_BASE_URL}/patents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patent)
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllPatents();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };
  const updatePatent = async (id, patent) => {
    try {
      const res = await fetch(`${API_BASE_URL}/patents/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patent)
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllPatents();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };
  const deletePatent = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/patents/${id}`, {
        method: 'DELETE'
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllPatents();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // --- PROJECTS CRUD HELPERS ---
  const fetchAllProjects = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/projects?all=true`);
      const json = await res.json();
      if (json.success) {
        setPortfolioData((prev) => ({ ...prev, projects: json.data }));
      }
    } catch {}
  };
  const addProject = async (project) => {
    try {
      const res = await fetch(`${API_BASE_URL}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(project)
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllProjects();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };
  const updateProject = async (id, project) => {
    try {
      const res = await fetch(`${API_BASE_URL}/projects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(project)
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllProjects();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };
  const deleteProject = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/projects/${id}`, {
        method: 'DELETE'
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllProjects();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // --- BLOG CRUD HELPERS ---
  const fetchAllBlogs = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/blog?all=true`);
      const json = await res.json();
      if (json.success) {
        setPortfolioData((prev) => ({ ...prev, blog: json.data }));
      }
    } catch {}
  };
  const addBlog = async (blog) => {
    try {
      const res = await fetch(`${API_BASE_URL}/blog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blog)
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllBlogs();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };
  const updateBlog = async (id, blog) => {
    try {
      const res = await fetch(`${API_BASE_URL}/blog/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blog)
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllBlogs();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };
  const deleteBlog = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/blog/${id}`, {
        method: 'DELETE'
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllBlogs();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // --- INNOVATIONS CRUD HELPERS ---
  const fetchAllInnovations = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/innovations?all=true`);
      const json = await res.json();
      if (json.success) {
        setPortfolioData((prev) => ({ ...prev, innovations: json.data }));
      }
    } catch {}
  };
  const addInnovation = async (innovation) => {
    try {
      const res = await fetch(`${API_BASE_URL}/innovations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(innovation)
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllInnovations();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };
  const updateInnovation = async (id, innovation) => {
    try {
      const res = await fetch(`${API_BASE_URL}/innovations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(innovation)
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllInnovations();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };
  const deleteInnovation = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/innovations/${id}`, {
        method: 'DELETE'
      });
      const json = await res.json();
      if (json.success) {
        await fetchAllInnovations();
        return { success: true };
      } else {
        return { success: false, error: json.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Update footer helper
  const updateFooter = async (footerData) => {
    try {
      const res = await fetch(`${API_BASE_URL}/footer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(footerData),
      });
      const data = await res.json();
      if (data.success) {
        setPortfolioData(prev => ({ ...prev, footer: data.data }));
      }
      return data;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  return (
    <PortfolioContext.Provider value={{
      portfolioData,
      groupedSkills,
      setGroupedSkills,
      updatePortfolioData,
      groupSkillsByCategory,
      flattenGroupedSkills,
      loading,
      error,
      addSkill,
      updateSkill,
      deleteSkill,
      addPatent,
      updatePatent,
      deletePatent,
      addProject,
      updateProject,
      deleteProject,
      addBlog,
      updateBlog,
      deleteBlog,
      addInnovation,
      updateInnovation,
      deleteInnovation,
      updateFooter,
    }}>
      {children}
    </PortfolioContext.Provider>
  );
};

export default PortfolioContext; 